/**
 * Hook para rastrear a plataforma de acesso do usuário (PWA vs Browser)
 * 
 * Detecta automaticamente se o app está rodando como PWA instalado
 * e registra essa informação no perfil do usuário no banco de dados.
 * 
 * Valores possíveis de plataforma:
 * - 'browser': Acesso via navegador web
 * - 'pwa': App instalado via PWA (Android/Desktop)
 * - 'pwa-ios': App instalado via PWA no iOS (Safari)
 * 
 * Executado uma única vez por sessão (fire-and-forget, sem impacto em performance).
 */
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para rastreamento de plataforma (só aparece em desenvolvimento)
const platformLogger = createLogger('PlatformTracker');

/**
 * Detecta a plataforma de acesso atual do usuário
 * @returns 'pwa-ios' | 'pwa' | 'browser'
 */
function detectPlatform(): string {
  // Verifica se está em modo standalone (app instalado)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;

  if (!isStandalone) {
    return 'browser';
  }

  // Diferencia PWA iOS de PWA Android/Desktop
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  return isIOS ? 'pwa-ios' : 'pwa';
}

/**
 * Hook que registra a plataforma de acesso do usuário no banco
 * Deve ser chamado após autenticação bem-sucedida
 * 
 * @param userId - ID do usuário autenticado (null se não autenticado)
 */
export function usePlatformTracker(userId: string | null): void {
  // Ref para garantir execução única por sessão
  const trackedRef = useRef(false);

  useEffect(() => {
    // Só rastrear se houver userId e ainda não tiver rastreado nesta sessão
    if (!userId || trackedRef.current) return;

    trackedRef.current = true;

    const platform = detectPlatform();
    platformLogger.info('Plataforma detectada', { platform, userId });

    // Server-side: substitui UPDATE direto em profiles por RPC track_my_platform_access
    // Fire-and-forget: não bloqueia UI
    supabase
      .rpc('track_my_platform_access', { p_platform: platform })
      .then(({ error }) => {
        if (error) {
          platformLogger.error('Erro ao registrar plataforma de acesso', error);
        } else {
          platformLogger.debug('Plataforma de acesso registrada com sucesso', { platform });
        }
      });
  }, [userId]);
}
